// Everything about a customer that can be counted from shipments, quotations and conversations.
import type { Shipment } from '@serb/shared';
import type { ActivityEvent } from '../communications/sample';
import { useCommunications } from '../communications/useCommunications';
import type { Quotation } from '../quotations/sample';
import { useQuotations } from '../quotations/useQuotations';
import { useShipments } from '../shipments/useShipments';
import type { Customer } from './sample';

/** What the numbers are counted from. Shipments, quotations and the message log are real. */
type Source = { ships: Shipment[]; quotes: Quotation[]; events: ActivityEvent[]; followUps: string[] };

const CLOSED = ['Lost', 'Cancelled', 'Merged'];
const same = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

/** The counts behind the numbers on the customer pages */
export function statsFrom(c: Customer, src: Source) {
  const ships = src.ships.filter((s) => same(s.customer, c.name));
  const srbs = ships.map((s) => s.srb);
  const quotes = src.quotes.filter((q) => srbs.includes(q.srb));
  // lost = the customer did not go ahead (quotation rejected or expired, or the shipment is lost or cancelled)
  const lostSrbs = [
    ...quotes.filter((q) => q.status === 'Rejected' || q.status === 'Expired').map((q) => q.srb),
    ...ships.filter((s) => CLOSED.includes(s.stage)).map((s) => s.srb),
  ];
  const arrived = ships.filter(
    (s) => (s.stage === 'Arrived' || s.stage === 'Completed') && !lostSrbs.includes(s.srb),
  ).length;
  const lost = new Set(lostSrbs).size;
  const total = ships.length;
  const waitingQuote = quotes.find((q) => q.status === 'Draft' || q.status === 'Sent');
  const events = src.events.filter((e) => srbs.includes(e.srb));
  return {
    ships,
    quotes,
    total,
    arrived,
    lost,
    active: total - arrived - lost,
    events,
    lastActivity: events[0]?.date ?? '—',
    waitingQuote,
    needsAttention: Boolean(waitingQuote) || src.followUps.some((srb) => srbs.includes(srb)),
  };
}
export type CustomerStats = ReturnType<typeof statsFrom>;

/** For the customer pages: counted from the real shipments in the vault */
export function useCustomerStats() {
  const { items } = useShipments();
  const quotes = useQuotations().items;
  const events = useCommunications().events;
  return (c: Customer) => statsFrom(c, { ships: items, quotes, events, followUps: [] });
}
